/**
 * Cross-field rules for pricing insight columns on system_settings.
 */

import { subcategories } from "@/lib/categoryStructure";

export type PricingInsightToggleRow = {
  global_pricing_insight_enabled?: boolean | null;
  pricing_insight_enabled?: boolean | null;
  user_pricing_insight_enabled?: boolean | null;
  seller_price_band_enforcement_enabled?: boolean | null;
  seller_price_band_recommendation_chips_enabled?: boolean | null;
  seller_price_chip_allowlist_enforced?: boolean | null;
  seller_price_chip_subcategory_ids?: readonly string[] | null;
};

const KNOWN_SELL_SUBCATEGORY_IDS = new Set(
  subcategories.map((subcategory) => subcategory.id),
);

export function isSellerPriceBandEnforcementOperational(
  row: PricingInsightToggleRow,
): boolean {
  return (
    Boolean(row.seller_price_band_enforcement_enabled) &&
    Boolean(row.global_pricing_insight_enabled) &&
    Boolean(row.user_pricing_insight_enabled)
  );
}

/** Apply dependent turn-offs when a parent toggle is disabled. */
export function applyPricingInsightSettingsCascades(
  current: PricingInsightToggleRow,
  patch: Partial<PricingInsightToggleRow>,
): PricingInsightToggleRow {
  const merged: PricingInsightToggleRow = { ...current, ...patch };

  if (merged.global_pricing_insight_enabled === false) {
    merged.pricing_insight_enabled = false;
    merged.user_pricing_insight_enabled = false;
    merged.seller_price_band_enforcement_enabled = false;
    merged.seller_price_band_recommendation_chips_enabled = false;
  }

  if (merged.user_pricing_insight_enabled === false) {
    merged.seller_price_band_enforcement_enabled = false;
    merged.seller_price_band_recommendation_chips_enabled = false;
  }

  if (merged.seller_price_band_enforcement_enabled === false) {
    merged.seller_price_band_recommendation_chips_enabled = false;
  }

  return merged;
}

export function isSellerPriceBandRecommendationChipsOperational(
  row: PricingInsightToggleRow,
): boolean {
  return (
    isSellerPriceBandEnforcementOperational(row) &&
    Boolean(row.seller_price_band_recommendation_chips_enabled)
  );
}

/** Drop blanks, duplicates, and ids that are not sell subcategories. */
export function normalizeSellerPriceChipSubcategoryIds(
  raw: readonly unknown[] | null | undefined,
): string[] {
  if (!raw?.length) return [];
  const seen = new Set<string>();
  const next: string[] = [];
  for (const value of raw) {
    if (typeof value !== "string") continue;
    const id = value.trim();
    if (!id || seen.has(id) || !KNOWN_SELL_SUBCATEGORY_IDS.has(id)) continue;
    seen.add(id);
    next.push(id);
  }
  next.sort();
  return next;
}

/** Add or remove one subcategory, then drop unknown ids. */
export function withSellerPriceChipSubcategoryMembership(
  current: readonly unknown[] | null | undefined,
  subcategoryId: string,
  enabled: boolean,
): string[] {
  const next = new Set(normalizeSellerPriceChipSubcategoryIds(current));
  const id = subcategoryId.trim();
  if (id) {
    if (enabled) next.add(id);
    else next.delete(id);
  }
  return normalizeSellerPriceChipSubcategoryIds([...next]);
}

/**
 * Only visibility rule for seller price chips.
 * Enforcement off matches the global chip switch and ignores the list.
 */
export function areSellerPriceChipsShown(params: {
  settings: PricingInsightToggleRow;
  subcategoryId?: string | null;
}): boolean {
  if (!isSellerPriceBandRecommendationChipsOperational(params.settings)) {
    return false;
  }
  if (!params.settings.seller_price_chip_allowlist_enforced) {
    return true;
  }
  const subcategoryId = params.subcategoryId?.trim();
  if (!subcategoryId) return false;
  return (params.settings.seller_price_chip_subcategory_ids ?? []).includes(
    subcategoryId,
  );
}

export function validatePricingInsightSettings(
  row: PricingInsightToggleRow,
): string | null {
  if (
    row.seller_price_band_enforcement_enabled &&
    !row.global_pricing_insight_enabled
  ) {
    return "Seller price band enforcement requires Global Price Analysis to be enabled.";
  }
  if (
    row.seller_price_band_enforcement_enabled &&
    !row.user_pricing_insight_enabled
  ) {
    return "Seller price band enforcement requires User Price Assistant to be enabled.";
  }
  return null;
}
